import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop component - handles scroll behavior intelligently:
 * - On page refresh: Restores the previous scroll position
 * - On navigation to new page: Scrolls to top
 * - Saves scroll position per route to sessionStorage
 */
const ScrollToTop = () => {
  const { pathname, key } = useLocation();
  const prevPathRef = useRef(pathname);
  const isFirstMount = useRef(true);
  
  // Save scroll position before unload or navigation
  useEffect(() => {
    const saveScrollPosition = () => {
      const scrollData = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}');
      scrollData[pathname] = window.scrollY;
      sessionStorage.setItem('scrollPositions', JSON.stringify(scrollData));
    };
    
    // Save on scroll (debounced via beforeunload)
    window.addEventListener('beforeunload', saveScrollPosition);
    
    // Also save periodically while user is on the page
    const intervalId = setInterval(saveScrollPosition, 1000);
    
    return () => {
      // Save when leaving this route
      saveScrollPosition();
      window.removeEventListener('beforeunload', saveScrollPosition);
      clearInterval(intervalId);
    };
  }, [pathname]);

  useEffect(() => {
    const scrollData = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}');
    const wasRefresh = sessionStorage.getItem('wasRefresh') === 'true';
    
    // Check if this is a refresh (same path) or a navigation (different path)
    const isRefresh = isFirstMount.current && wasRefresh && prevPathRef.current === pathname;
    const isSamePathNavigation = !isFirstMount.current && prevPathRef.current === pathname;
    
    if (isRefresh || isSamePathNavigation) {
      // Restore scroll position on refresh
      const savedPosition = scrollData[pathname] || 0;
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, savedPosition);
      });
    } else if (!isFirstMount.current) {
      // New navigation - scroll to top
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } else if (isFirstMount.current && scrollData[pathname]) {
      // First mount with saved position (likely a refresh)
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollData[pathname]);
      });
    }
    
    prevPathRef.current = pathname;
    isFirstMount.current = false;
    
    // Clear refresh flag
    sessionStorage.removeItem('wasRefresh');
  }, [pathname, key]);
  
  // Set refresh flag when page is about to reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('wasRefresh', 'true');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return null;
};

export default ScrollToTop;
